import { addValidationRule } from 'formsy-react'
import { I18n } from 'react-redux-i18n'

/**
 * Validates that a Brame campaign URL matches one of two supported formats:
 *   - Advanced games: https://campaigns.brame.io/{uuid}/{uuid}/
 *   - Basic games:    https://live.brame-gamification.com/{uuid}/
 *
 * Both formats require https + a trailing slash. The id segments accept
 * lowercase hex characters and hyphens (permissive UUID-ish shape).
 *
 * Keep this pattern in sync with nexus' Brame::Campaign format validation
 * (nucleus/app/models/brame/campaign.rb).
 */
const BRAME_CAMPAIGN_URL_REGEX =
  /^https:\/\/(campaigns\.brame\.io\/[0-9a-f-]+\/[0-9a-f-]+\/|live\.brame-gamification\.com\/[0-9a-f-]+\/)$/i

/**
 * Auto-corrects a BRAME campaign URL by adding a trailing slash if it's missing
 * but otherwise valid. Returns the corrected URL or the original URL if no correction
 * is needed. Applies to both supported Brame URL shapes.
 */
export const autoCorrectBrameCampaignUrl = (url: string): string => {
  if (!url) return url

  // If it already passes validation, no correction needed
  if (BRAME_CAMPAIGN_URL_REGEX.test(url)) {
    return url
  }

  // If it's missing only the trailing slash, add it
  if (BRAME_WITHOUT_TRAILING_SLASH_REGEX.test(url)) {
    return url + '/'
  }

  // Otherwise, return the original URL (other validation errors)
  return url
}

// Additional regex patterns for specific error detection
const BRAME_BASE_URL_REGEX =
  /^https:\/\/(campaigns\.brame\.io|live\.brame-gamification\.com)\//i
const BRAME_CAMPAIGNS_BASE_URL_REGEX = /^https:\/\/campaigns\.brame\.io\//i
const BRAME_LIVE_BASE_URL_REGEX = /^https:\/\/live\.brame-gamification\.com\//i
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
// Matches either supported Brame URL shape minus the required trailing slash.
// Used by autoCorrectBrameCampaignUrl to repair pasted URLs that are otherwise
// well-formed.
const BRAME_WITHOUT_TRAILING_SLASH_REGEX =
  /^https:\/\/(campaigns\.brame\.io\/[0-9a-f-]+\/[0-9a-f-]+|live\.brame-gamification\.com\/[0-9a-f-]+)$/i

addValidationRule('isBrameCampaignUrl', (values: any, value: string) => {
  if (!value) return true // Let isRequired handle empty values

  // Check if it's a valid Brame campaign URL with trailing slash
  if (BRAME_CAMPAIGN_URL_REGEX.test(value)) {
    return true
  }

  // Check if it's missing only the trailing slash - this should now pass validation
  // since we'll auto-correct it in the form
  if (BRAME_WITHOUT_TRAILING_SLASH_REGEX.test(value)) {
    return true // Auto-correction will handle adding the trailing slash
  }

  // Provide specific error messages for other issues

  // Check if it doesn't start with one of the supported base URLs
  if (!BRAME_BASE_URL_REGEX.test(value)) {
    return I18n.t('validations.brame_url_invalid_domain')
  }

  // Format errors specific to the campaigns.brame.io shape
  if (BRAME_CAMPAIGNS_BASE_URL_REGEX.test(value)) {
    const urlPath = value.substring('https://campaigns.brame.io/'.length)
    const parts = urlPath.split('/')

    // Should have exactly 3 parts: uuid, uuid, empty (for trailing slash)
    if (parts.length !== 3 || parts[2] !== '') {
      return I18n.t('validations.brame_url_invalid_format')
    }

    // Check if UUIDs are valid format
    if (!UUID_PATTERN.test(parts[0]) || !UUID_PATTERN.test(parts[1])) {
      return I18n.t('validations.brame_url_invalid_campaign_ids')
    }
  }

  // Format errors specific to the live.brame-gamification.com shape
  if (BRAME_LIVE_BASE_URL_REGEX.test(value)) {
    const urlPath = value.substring(
      'https://live.brame-gamification.com/'.length
    )
    const parts = urlPath.split('/')

    // Should have exactly 2 parts: id, empty (for trailing slash)
    if (parts.length !== 2 || parts[1] !== '') {
      return I18n.t('validations.brame_url_invalid_format')
    }

    if (!UUID_PATTERN.test(parts[0])) {
      return I18n.t('validations.brame_url_invalid_campaign_ids')
    }
  }

  // Default error for other cases
  return I18n.t('validations.must_be_valid_brame_campaign_url')
})

export { BRAME_CAMPAIGN_URL_REGEX }
